/* Key ingredients slider. Advances on its own every `data-interval` ms with a
   horizontal slide; pauses while a finger or pointer is on it and when the tab
   is hidden; swipes; loops. Slides whose image failed to load have already
   removed themselves from the DOM, so it never shows an empty frame. */
class AnyraIngredientSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.anyra-ings__track');
    this.viewport = this.querySelector('.anyra-ings__viewport');
    this.dotsEl = this.querySelector('[data-ings-dots]');
    this.interval = parseInt(this.dataset.interval || '4000', 10);
    this.index = 0;
    this.timer = null;
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /* Images that 404 remove their slide asynchronously; build the dot row and
       decide whether to run only after the first paint has settled. */
    requestAnimationFrame(() => setTimeout(() => this.init(), 250));
  }

  slides() { return Array.from(this.track.children); }

  init() {
    const n = this.slides().length;
    if (n === 0) { this.hidden = true; return; }
    this.buildDots();
    this.go(0, false);
    if (n < 2) {
      this.querySelectorAll('.anyra-ings__arrow').forEach(a => a.remove());
      if (this.dotsEl) this.dotsEl.remove();
      return;
    }
    this.bind();
    this.start();
  }

  buildDots() {
    if (!this.dotsEl) return;
    this.dotsEl.innerHTML = '';
    this.slides().forEach((_, i) => {
      const d = document.createElement('button');
      d.type = 'button';
      d.className = 'anyra-ings__dot';
      d.setAttribute('aria-label', 'Go to ingredient ' + (i + 1));
      d.addEventListener('click', () => { this.go(i); this.restart(); });
      this.dotsEl.appendChild(d);
    });
  }

  go(i, animate = true) {
    const n = this.slides().length;
    if (!n) return;
    this.index = (i + n) % n;
    if (!animate || this.reduced) this.track.style.transition = 'none';
    this.track.style.transform = 'translateX(' + (-100 * this.index) + '%)';
    /* Slides are lazy. Ask for the next one now so the transition never lands
       on a blank frame. */
    const nxt = this.slides()[(this.index + 1) % n];
    const img = nxt && nxt.querySelector('img');
    if (img && img.loading === 'lazy') img.loading = 'eager';
    if (!animate || this.reduced) requestAnimationFrame(() => { this.track.style.transition = ''; });
    if (this.dotsEl) Array.from(this.dotsEl.children).forEach((d, k) => d.classList.toggle('is-active', k === this.index));
  }

  next() { this.go(this.index + 1); }
  prev() { this.go(this.index - 1); }

  start() {
    if (this.reduced) return;
    this.stop();
    this.timer = setInterval(() => this.next(), this.interval);
  }
  stop() { if (this.timer) { clearInterval(this.timer); this.timer = null; } }
  restart() { this.stop(); this.start(); }

  bind() {
    this.querySelector('[data-ings-next]')?.addEventListener('click', () => { this.next(); this.restart(); });
    this.querySelector('[data-ings-prev]')?.addEventListener('click', () => { this.prev(); this.restart(); });

    this.viewport.addEventListener('pointerenter', () => this.stop());
    this.viewport.addEventListener('pointerleave', () => this.start());

    let x0 = null;
    this.viewport.addEventListener('touchstart', e => { x0 = e.touches[0].clientX; this.stop(); }, { passive: true });
    this.viewport.addEventListener('touchend', e => {
      if (x0 === null) return;
      const dx = e.changedTouches[0].clientX - x0;
      if (Math.abs(dx) > 40) (dx < 0 ? this.next() : this.prev());
      x0 = null;
      this.start();
    }, { passive: true });

    document.addEventListener('visibilitychange', () => document.hidden ? this.stop() : this.start());

    /* Only run while on screen. Off-screen autoplay is wasted work and, on a
       long page, a battery cost the shopper never sees. */
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(entries => {
        entries.forEach(en => en.isIntersecting ? this.start() : this.stop());
      }, { threshold: 0.25 }).observe(this);
    }
  }
}
if (!customElements.get('anyra-ingredient-slider')) customElements.define('anyra-ingredient-slider', AnyraIngredientSlider);
